import React, { useEffect, useMemo, useState } from "react";

// API e contexto
import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

// react-table
import { useTable, useFilters, useSortBy } from "react-table";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageHeader from "pages/components/PageHeader";

// MUI
import { Card, CircularProgress, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

const TABLE_KEY = "fabricantes-fornecedores";
const LS_FILTERS_KEY = `tbl:${TABLE_KEY}:filters`;

function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;

  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "2px 4px",
        fontSize: 11,
        border: "none",
        borderBottom: "1px solid #e0e0e0",
        outline: "none",
        backgroundColor: "transparent",
      }}
      placeholder="Filtrar..."
    />
  );
}

function FabricantesFornecedores() {
  const { showAlert } = useAlert();
  const { temFuncao } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [fabricantes, setFabricantes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);

  const [fabricanteSel, setFabricanteSel] = useState(null);
  const [fornecedorSel, setFornecedorSel] = useState(null);

  const [inputFabricante, setInputFabricante] = useState("");
  const [inputFornecedor, setInputFornecedor] = useState("");

  const carregarTabela = async () => {
    setLoading(true);

    try {
      const res = await api.post("Materiais/SelFabricantesFornecedores", { q: "" });
      const resposta = res.data;
      const dados = Array.isArray(resposta) ? resposta : resposta?.data || [];

      setRows(dados);
    } catch (err) {
      handleApi(err, showAlert);
    } finally {
      setLoading(false);
    }
  };

  const carregarFabricantes = async (q = "") => {
    try {
      const res = await api.post("Materiais/SelFabricantesAssociacaoFornecedor", { q });
      const resposta = res.data;
      const dados = Array.isArray(resposta) ? resposta : resposta?.data || [];

      setFabricantes(dados);
    } catch (err) {
      handleApi(err, showAlert);
    }
  };

  const carregarFornecedores = async (q = "") => {
    try {
      const res = await api.post("Materiais/SelFornecedoresAssociacaoFabricante", { q });
      const resposta = res.data;
      const dados = Array.isArray(resposta) ? resposta : resposta?.data || [];

      setFornecedores(dados);
    } catch (err) {
      handleApi(err, showAlert);
    }
  };

  const associar = async () => {
    if (!fabricanteSel?.ref) {
      showAlert("Selecione um fabricante.", "warning");
      return;
    }

    if (!fornecedorSel?.ref) {
      showAlert("Selecione um fornecedor.", "warning");
      return;
    }

    setLoading(true);

    try {
      const res = await api.post("Materiais/UpdFabricanteFornecedor", {
        reffabricante: fabricanteSel.ref,
        reffornecedor: fornecedorSel.ref,
      });

      const resposta = res.data;
      const tipo = resposta?.success ? "success" : "error";

      showAlert(resposta?.message || "Associação gravada.", tipo);

      if (resposta?.success) {
        await carregarTabela();
        await carregarFabricantes(inputFabricante || "");

        setFornecedorSel(null);
        setInputFornecedor("");
      }
    } catch (err) {
      handleApi(err, showAlert);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdminLike) return;

    carregarTabela();
    carregarFabricantes("");
    carregarFornecedores("");
  }, [isAdminLike]);

  useEffect(() => {
    if (!isAdminLike) return;

    const t = setTimeout(() => {
      carregarFabricantes(inputFabricante || "");
    }, 250);

    return () => clearTimeout(t);
  }, [inputFabricante, isAdminLike]);

  useEffect(() => {
    if (!isAdminLike) return;

    const t = setTimeout(() => {
      carregarFornecedores(inputFornecedor || "");
    }, 250);

    return () => clearTimeout(t);
  }, [inputFornecedor, isAdminLike]);

  const data = useMemo(() => rows, [rows]);

  const columns = useMemo(
    () => [
      {
        Header: "Ref Fabricante",
        accessor: "reffabricante",
      },
      {
        Header: "Fabricante",
        accessor: "nomefabricante",
      },
      {
        Header: "Ref Fornecedor",
        accessor: "reffornecedor",
      },
      {
        Header: "Fornecedor",
        accessor: "nomefornecedor",
      },
    ],
    []
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const initialFilters = useMemo(() => {
    try {
      const raw = localStorage.getItem(LS_FILTERS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, []);

  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
    state,
  } = tableInstance;

  useEffect(() => {
    try {
      localStorage.setItem(LS_FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters]);

  if (!isAdminLike) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDTypography variant="h6">Sem permissão para aceder a esta página.</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <PageHeader />

        <MDBox mt={2}>
          <Card>
            <MDBox p={2}>
              <MDBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr auto" }}
                gap={2}
                alignItems="end"
              >
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" mb={0.5} display="block">
                    Fabricante
                  </MDTypography>

                  <Autocomplete
                    options={fabricantes}
                    filterOptions={(x) => x}
                    value={fabricanteSel}
                    onChange={(event, newValue) => setFabricanteSel(newValue)}
                    inputValue={inputFabricante}
                    onInputChange={(event, newInputValue) => setInputFabricante(newInputValue)}
                    getOptionLabel={(option) =>
                      option ? `${option.ref || ""} - ${option.nome || ""}` : ""
                    }
                    isOptionEqualToValue={(option, value) => option?.ref === value?.ref}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </MDBox>

                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" mb={0.5} display="block">
                    Fornecedor
                  </MDTypography>

                  <Autocomplete
                    options={fornecedores}
                    filterOptions={(x) => x}
                    value={fornecedorSel}
                    onChange={(event, newValue) => setFornecedorSel(newValue)}
                    inputValue={inputFornecedor}
                    onInputChange={(event, newInputValue) => setInputFornecedor(newInputValue)}
                    getOptionLabel={(option) =>
                      option ? `${option.ref || ""} - ${option.nome || ""}` : ""
                    }
                    isOptionEqualToValue={(option, value) => option?.ref === value?.ref}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </MDBox>

                <MDBox>
                  <MDButton color="success" onClick={associar} disabled={loading}>
                    Associar
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        <MDBox mt={2}>
          <Card>
            <MDBox p={2}>
              {loading ? (
                <MDBox display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </MDBox>
              ) : (
                <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
                  <table
                    {...getTableProps()}
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      {headerGroups.map((hg) => (
                        <tr {...hg.getHeaderGroupProps()}>
                          {hg.headers.map((col) => (
                            <th
                              {...col.getHeaderProps(col.getSortByToggleProps())}
                              style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                background: "#fff",
                                textAlign: "left",
                                padding: "6px 8px",
                                fontSize: 12,
                                color: "#7b809a",
                                verticalAlign: "bottom",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center" }}>
                                {col.render("Header")}
                              </div>

                              {!col.disableFilters && (
                                <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                  {col.canFilter ? col.render("Filter") : null}
                                </div>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                      {tableRows.map((row) => {
                        prepareRow(row);

                        return (
                          <tr
                            {...row.getRowProps()}
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                              transition: "background-color 0.15s",
                            }}
                          >
                            {row.cells.map((cell) => (
                              <td
                                {...cell.getCellProps()}
                                style={{
                                  padding: "6px 8px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })}

                      {tableRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={columns.length}
                            style={{
                              padding: "12px 8px",
                              textAlign: "center",
                              fontSize: 12,
                              color: "#9e9e9e",
                            }}
                          >
                            Sem associações registadas.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default FabricantesFornecedores;
